// Punktlista — UI strings, Swedish and English. The digest content itself
// (report summaries) is Swedish only for now; this covers the chrome.
//
// Vocabulary (docs/plan/2026-09-13-follow-anything-plan.md): the thing a
// reader follows is a "källa" / "source", the act is "följa" / "follow", and
// what they get is "tre punkter" / "three bullets" in one "utskick" /
// "digest". "Bevakning" is gone from every reader-facing string below — the
// JS keys keep their old names (tabWatches, watchAction, ownFeedTag, ...) so
// every file that reads them is untouched, only the printed words changed.

export const RV_STRINGS = {
  sv: {
    tabDigest: 'Utskick',
    tabWatches: 'Källor',
    tabPlan: 'Din plan',
    domain: 'bostad och samhällsbyggnad',
    about: 'Om tjänsten',
    myDeliveries: 'Mina utskick',
    open: 'Öppna Punktlista',
    reportPage: 'Gå till källan',
    link: 'Länk',
    // The collapsible-quote toggle on a point's citation row (Delivery.jsx).
    // The visible label always says "Referens" - these two are read out by
    // assistive tech instead, since they name the action, not the thing.
    referens: 'Referens',
    showQuote: 'Visa citatet',
    hideQuote: 'Dölj citatet',
    searchPlaceholder: 'Sök källa eller avsändare',
    filterAll: 'Alla',
    noMatches: 'Inga källor matchar.',
    yourWatch: 'Du följer',
    unlock: 'Lås upp',
    pickFree: 'Välj gratis',
    // Every pick past the first free one — a second-and-later source, or any
    // source at all on a paid plan — says this instead of "Välj gratis",
    // since nothing about it is free.
    watchAction: 'Följ',
    activate: 'Aktivera',
    close: 'Stäng',
    openPage: 'Öppna sidan',
    catalogIntro: 'Den första källan är gratis. Fler kräver ett abonnemang.',
    // Same line, phrased for a reader who already pays: what applies to them
    // is their tier's ceiling, not the free plan's one-pick rule.
    catalogIntroFler: 'Du kan följa upp till 5 källor.',
    catalogIntroObegransat: 'Du kan följa så många källor du vill.',
    planFree: 'Din plan i dag: gratis',
    planTitle: 'Din plan',
    planGratisName: 'Gratis',
    planGratisLimit: '1 källa',
    planPeriod: 'per månad',
    planCurrentTag: 'Din plan i dag',
    planWatchLine: (report, publisher, email) =>
      `Din första källa är ${report} från ${publisher}. Utskicken går till ${email}.`,
    planWatchCountLine: (count, email) =>
      `Du följer ${count} ${count === 1 ? 'källa' : 'källor'}. Utskicken går till ${email}.`,
    remove: 'Ta bort',
    removeWatch: 'Ta bort källan',
    removeTitle: 'Ta bort källan?',
    // A second argument, not a second string key: the "you can pick a new
    // free source right away" clause is only true on the free plan, where
    // removing a source reopens the one free slot. A paid plan's slot is
    // still there — removing a source does not need saying so.
    removeBody: (name, isFreePlan) =>
      isFreePlan
        ? `${name} slutar skickas till dig. Du kan välja en ny källa gratis direkt efteråt.`
        : `${name} slutar skickas till dig.`,
    cancel: 'Avbryt',
    // Split around the link to Källor, which is a real route now.
    planChangeBefore: 'Vill du byta källa gör du det själv under ',
    planChangeAfter: '.',
    planPickBefore: 'Du har inte valt din första källa än. Gör det under ',
    planPickAfter: '.',
    // A paying reader with nothing followed yet has no "free source" to pick.
    planEmptyBefore: 'Du följer inget än. Välj under ',
    planEmptyAfter: '.',
    // --- sign in / sign out — a real magic-link session now
    // (docs/plan/2026-09-11-magic-link-login.md). The control itself lives
    // in the top bar (Shell.jsx's AccountMenu/LoginControl).
    accountLabel: 'Konto',
    logoutBtn: 'Logga ut',
    loginBtn: 'Logga in',
    loginEmailLabel: 'Din mejladress',
    loginPlaceholder: 'fornamn.efternamn@företag.se',
    loginHint: 'Ange mejladressen du följer med så visas dina källor.',
    loginError: 'Skriv en mejladress som går att skicka till.',
    // Magic-link login (docs/plan/2026-09-11-magic-link-login.md): shown
    // after a link has actually been mailed, wherever a form asked for one —
    // the top bar's popover, a catalogue row, /logga-in, the add-source sheet.
    loginSent: (email) => `Vi har mejlat en länk till ${email}. Öppna den för att logga in.`,
    resend: 'Skicka igen',
    sending: 'Skickar',
    loginVerifying: 'Loggar in…',
    loginLinkFailed: 'Länken har gått ut eller är redan använd.',
    // The Din plan tab's own nudge for a reader who has not signed in yet —
    // the tab itself carries no sign-in form any more, just this sentence.
    planLoginNudge: 'Logga in uppe till höger så visas din plan och dina källor.',
    publishers: 'Utgivare',
    publishersTitle: 'Utgivare som följs',
    publishersIntro: (publishers, series) =>
      `${publishers} avsändare inom bostad och samhällsbyggnad, tillsammans ${series} källor. Varje ny utgåva läses och sammanfattas till tre punkter.`,
    publisherSeriesHead: 'Källor som följs',
    publisherSamplesHead: 'Utskick från den här utgivaren',
    publisherPitch:
      'När en av källorna uppdateras får du de tre punkter som ändrar en bedömning. Varje punkt har sitt sidnummer och en länk rakt in i pdf:en.',
    publisherCta: 'Se alla källor',
    allPublishers: 'Alla utgivare',
    footer: 'en tjänst från Utforska Data',
    // The promise, not the catalogue. The domain we follow today is on the
    // publisher pages and in the catalogue itself; a footer that names it turns
    // stale the day the second domain lands.
    footerLine: 'Det viktigaste ur allt du följer, i tre punkter med källhänvisning.',
    footerService: 'Tjänsten',
    footerSamples: 'Exempelutskick',
    footerContact: 'Kontakt',
    // Both this and the help link above it point at /om/: the address
    // itself is never printed here (see Shell.jsx's footer comment), so this
    // is a second, warmer way to the one place it is offered.
    writeToOsama: 'Skriv till Osama',
    // Points at /om/#faq — the FAQ section on the same about page, so a
    // reader with a question can jump straight to it from the footer.
    helpFaqLink: 'Vanliga frågor',
    footerRights: '© 2026 Punktlista',
    // --- utskick tab ---
    watchStatusHead: 'Din källa',
    // The cadence string is quoted verbatim from the catalogue. Nothing here
    // may turn it into a date: we do not know when the source next updates.
    watchActive: (cadence) =>
      `Du följer den här källan. Nästa utskick kommer nästa gång källan uppdateras (”${cadence}”).`,
    firstDeliveryNote:
      'Ditt första utskick kommer nästa gång källan uppdateras, inte tidigare.',
    firstDeliveryLink: 'Så funkar utskicken',
    samplesHead: 'Exempelutskick',
    // The reader's own latest edition, shown above the rendered digest.
    latestDelivery: 'Senaste utskicket',
    sendToMe: 'Skicka till mig',
    sentTo: (email) => `Skickat till ${email}.`,
    // --- about page (was the help page; renamed 2026-09) ---
    // `help` is the nav/footer link label, short since it sits next to other
    // one-word links; `helpTitle` is the page's own <h1> and stays the full
    // name.
    help: 'Om',
    helpTitle: 'Om Punktlista',
    helpGreeting: 'Hej! Det är jag som har byggt den här tjänsten.',
    helpIntro:
      'Efter att ha byggt andra verktyg insåg jag att det som släpps därute är fullt av intressant information som få hinner läsa. Så jag byggde något som ger mig själv och andra tre punkter ur allt vi följer. Prova gärna om du inte redan gjort det! Frågor och funderingar går bra att skicka till min mejladress.',
    helpSign: 'Osama',
    helpMailCta: 'Mejla mig',
    helpPhotoAlt: 'Osama Eldawebi, som bygger Punktlista',
    helpFaqHead: 'Vanliga frågor',
    helpFaq: [
      {
        q: 'Hur funkar det?',
        a: 'Du väljer en källa under Källor och anger din mejladress. Nästa gång källan uppdateras läser vi den och mejlar dig tre punkter ur den, var och en med sitt sidnummer och en länk som öppnar pdf:en på rätt sida.',
      },
      {
        q: 'När kommer mitt första utskick?',
        a: 'När källan du valt uppdateras nästa gång. Hur ofta det sker skiljer sig mellan källorna, från varje månad till en gång om året, och det står på varje rad i listan under Källor. Innan du valt en källa visar appen några riktiga utskick från källor vi redan följer.',
      },
      {
        q: 'Vad kostar det?',
        a: 'Den första källan är gratis och kräver bara en mejladress. Vill du följa fler källor finns två nivåer med fast månadspris. De står under Din plan, som du når uppe i menyn. Ingen bindningstid.',
      },
      {
        q: 'Hur säger jag upp?',
        a: 'Den fria källan tar du bort själv under Källor, då slutar utskicken direkt. Ett betalt abonnemang avslutar du i Stripes kundportal, som du når från kvittomejlet. Eller mejla mig, så gör jag det åt dig.',
      },
      {
        q: 'Saknas en källa du vill följa?',
        a: 'Klistra in sidan i appen under Källor. Funkar inte det kan du skicka ett önskemål direkt där. Att lägga till en ny källa för hand brukar ta mig en dag.',
      },
    ],
    // The hook, one sentence, one line. `heroMark` is the slice of heroTitle
    // the landing page paints with the marker yellow, so the highlight
    // follows the copy instead of living as markup in the page.
    heroTitle: 'Tre punkter ur allt du följer',
    heroMark: 'tre punkter',
    heroCta: 'Välj din första källa',
    proofHead: 'Så här ser ett utskick ut',
    wallLabel: 'Avsändare som följs',
    seeSample: 'Se ett utskick',
    mailSubject: (title) => `Tre punkter ur ${title}`,
    stripCaption: '19 källor från 15 avsändare i dag.',
    closeTitle: 'Första källan är gratis.',
    priceNote: 'Hela bostadsområdet. Ingen bindningstid.',
    // Split around the link into the help page. No address here: a question in
    // a payment sheet belongs on the page that answers it.
    sheetFoot: 'Ingen bindningstid. Betalningen går via Stripe. Frågor? ',
    helpPageLink: 'Se vår hjälpsida.',
    startHead: 'Kom igång',
    startSteps: [
      'Välj en källa',
      'Ange din mejl',
      'Få nästa utskick',
    ],
    welcomeLine: 'Välj din första källa, den är gratis.',
    activating: 'Aktiverar',
    removing: 'Tar bort',
    pickDone: (report, publisher, email) =>
      `Klart! Du får nästa utskick av ${report} från ${publisher} till ${email}.`,
    // Signed-out pick: nothing is followed yet — the reader has only asked
    // for a login link that will activate it once clicked.
    pickCheckMail: (email) =>
      `Kolla mejlen: vi har skickat en länk till ${email} som aktiverar källan.`,
    offlineNote: 'Vi når inte servern just nu. Din källa visas som den var senast.',
    notFoundTitle: 'Sidan finns inte.',
    notFoundBody: 'Adressen är felstavad, eller så har sidan flyttat eller tagits bort.',
    notFoundHome: 'Till startsidan',
    notFoundPublishers: 'Se avsändare som följs',
    // --- own sources (T6.3, generalised in the follow-anything plan) ---
    ownFeedHead: 'Följ en källa',
    // Opens the sheet that holds OwnFeedForm; same words as ownFeedHead
    // (the sheet's title), since the button says what it is about to open.
    ownFeedOpen: 'Följ en källa',
    ownFeedHint:
      'Klistra in adressen till en källa du vill följa, till exempel en myndighets nyhetssida eller ett pressrum. Vi hittar det nya åt dig.',
    ownFeedUrlPlaceholder: 'https://exempel.se/nyheter',
    ownFeedUrlLabel: 'Adress till källan',
    ownFeedEmailLabel: 'Mejladress för källan',
    ownFeedFreqLabel: 'Hur ofta',
    ownFeedSubmit: 'Lägg till',
    ownFeedPending: 'Kollar källan …',
    ownFeedTag: 'Egen källa',
    ownFeedEmptyUrl: 'Klistra in adressen till källan.',
    // Signed-out sheet body: no source can be added yet, only a login link
    // requested (see OwnFeedLoginForm in RapportvaktPage.jsx).
    ownFeedLoginNudge: 'Logga in för att följa egna källor.',
    // The add-source preview card (C4): shown once the pasted address
    // resolves to a host we know we cannot read yet. Submit stays disabled.
    sourceNotSupported: 'Den här källan stöds inte än.',
    // The add-source preview when the pasted address is a kind we already
    // know is coming (RAPPORTVAKT_UPCOMING_KINDS) rather than merely
    // unsupported — x.com, twitter.com, linkedin.com. `name` is that kind's
    // label. Submit still stays disabled.
    upcomingKind: (name) => `${name} kommer snart. Säg till om du vill ha det först.`,
    // --- coming soon (sources tab, C-upcoming) ---
    upcomingHead: 'Kommer snart',
    // One line, ending in the link (upcomingRequestLink) rather than a
    // separate "Saknar du en avsändare?" line underneath — only one link in
    // the coming-soon section (Osama's phone review, 2026-09).
    upcomingNote: 'Går att önska redan nu. ',
    upcomingRequestLink: 'Skicka ett önskemål',
    // --- series requests ---
    requestHead: 'Vill du att jag lägger till den för hand?',
    requestNotePlaceholder: 'Vad heter avsändaren eller källan?',
    requestNoteLabel: 'Berätta mer',
    requestSubmit: 'Skicka önskemål',
    requestPending: 'Skickar …',
    requestThanks: 'Tack! Jag hör av mig när källan går att följa.',
    requestOpen: 'Saknar du en avsändare? Skicka ett önskemål',
    requestNeedNote: 'Berätta vilken avsändare du saknar.',
    // --- plan table / upgrade sheet (RapportvaktPage.jsx) ---
    chooseTier: (name) => `Välj ${name}`,
    unlockTitle: (report) => `Lås upp ${report ?? 'fler källor'}`,
    unlockBody: (name) =>
      `Du har redan en källa gratis. ${name ?? 'Den här källan'} ingår i båda nivåerna nedan.`,
    // Joins a report and its publisher into one name, e.g. in the remove and
    // upgrade sheets: `${report}${t.fromJoiner}${publisher}`.
    fromJoiner: ' från ',
    emailForLabel: (report) => `Mejladress för ${report}`,
    // --- sample and digest pages (RapportvaktSamplePage.jsx, RapportvaktDigestPage.jsx) ---
    sampleIntroStrong: 'Det här är ett riktigt utskick från Punktlista.',
    sampleIntroBody:
      ' När en källa uppdateras får prenumeranter de tre viktigaste punkterna, var och en med sidnummer och en länk som öppnar pdf:en på rätt sida.',
    moreExamples: 'Fler exempel:',
    andJoiner: ' och ',
    ctaTitle: 'Vill du ha det här för din källa?',
    ctaBody: (count, amount, period) =>
      `${count} källor inom bostad och samhällsbyggnad följs i dag. Första källan är gratis, därefter ${amount} ${period}. Frågor? `,
    weeklyNote:
      'Varje måndag: tre punkter ur förra veckans statistiknyheter från SCB, med citat och direktlänk. Källa: SCB.',
    weeklyAllWeek: (week, count) => `Allt SCB publicerade vecka ${week} (${count} statistiknyheter)`,
    weeklyAll: (count) => `Allt SCB publicerade den här veckan (${count} statistiknyheter)`,
    prevWeek: 'Föregående vecka',
    nextWeek: 'Nästa vecka',
    otherWeeks: 'Andra veckor',
    // --- point visual (PointVisual.jsx) ---
    benchmarkLabel: 'Riktmärke: ',
  },
  // English. The Swedish block above is the source; this is a rewrite in the
  // words an English-speaking reader would expect, not a word-for-word
  // translation. You follow a source, and what you get is a digest.
  en: {
    tabDigest: 'Digests',
    tabWatches: 'Sources',
    tabPlan: 'Your plan',
    domain: 'Swedish housing and construction',
    about: 'About',
    myDeliveries: 'My digests',
    open: 'Open Punktlista',
    reportPage: 'Go to the source',
    link: 'Link',
    referens: 'Reference',
    showQuote: 'Show quote',
    hideQuote: 'Hide quote',
    searchPlaceholder: 'Search sources or publishers',
    filterAll: 'All',
    noMatches: 'No sources match.',
    yourWatch: 'You follow this',
    unlock: 'Unlock',
    pickFree: 'Follow for free',
    watchAction: 'Follow',
    activate: 'Activate',
    close: 'Close',
    openPage: 'Open page',
    catalogIntro: 'Your first source is free. To follow more, pick a plan.',
    catalogIntroFler: 'You can follow up to 5 sources.',
    catalogIntroObegransat: 'You can follow as many sources as you like.',
    planFree: 'Current plan: Free',
    planTitle: 'Your plan',
    planGratisName: 'Free',
    planGratisLimit: '1 source',
    planPeriod: 'per month',
    planCurrentTag: 'Current plan',
    planWatchLine: (report, publisher, email) =>
      `Your first source is ${report} from ${publisher}. Digests go to ${email}.`,
    // "source" takes a plain plural in English, unlike Swedish "källa"/"källor".
    planWatchCountLine: (count, email) =>
      `You follow ${count} ${count === 1 ? 'source' : 'sources'}. Digests go to ${email}.`,
    remove: 'Remove',
    removeWatch: 'Stop following',
    removeTitle: 'Stop following this source?',
    removeBody: (name, isFreePlan) =>
      isFreePlan
        ? `You will no longer get ${name}. You can pick a new free source right away.`
        : `You will no longer get ${name}.`,
    cancel: 'Cancel',
    planChangeBefore: 'To follow a different source, go to ',
    planChangeAfter: '.',
    // --- sign in / sign out ---
    accountLabel: 'Account',
    logoutBtn: 'Log out',
    loginBtn: 'Log in',
    loginEmailLabel: 'Your email address',
    loginPlaceholder: 'firstname.lastname@company.com',
    loginHint: 'Enter the email you signed up with to see the sources you follow.',
    loginError: 'Enter a valid email address.',
    loginSent: (email) => `We sent a link to ${email}. Open it to log in.`,
    resend: 'Resend',
    sending: 'Sending',
    loginVerifying: 'Logging in…',
    loginLinkFailed: 'This link has expired or has already been used.',
    planLoginNudge: 'Log in at the top right to see your plan and the sources you follow.',
    planPickBefore: 'You have not picked your free source yet. Do that under ',
    planPickAfter: '.',
    planEmptyBefore: 'You are not following anything yet. Pick a source under ',
    planEmptyAfter: '.',
    publishers: 'Publishers',
    publishersTitle: 'Publishers we follow',
    publishersIntro: (publishers, series) =>
      `${publishers} publishers in Swedish housing and construction, ${series} sources in all. Every new edition is read and summed up in three points.`,
    publisherSeriesHead: 'Sources we follow',
    publisherSamplesHead: 'Digests from this publisher',
    publisherPitch:
      'When one of these sources updates, you get the three points that change the picture. Each one has its page number and a link straight into the pdf.',
    publisherCta: 'See all sources',
    allPublishers: 'All publishers',
    footer: 'a service from Utforska Data',
    footerLine: 'Everything you follow, boiled down to three bullet points, each with its source.',
    footerService: 'The service',
    footerSamples: 'Sample digests',
    footerContact: 'Contact',
    writeToOsama: 'Write to Osama',
    helpFaqLink: 'Common questions',
    footerRights: '© 2026 Punktlista',
    // --- digests tab ---
    watchStatusHead: 'Your source',
    watchActive: (cadence) =>
      `You are all set. The next digest arrives next time the source updates (“${cadence}”).`,
    firstDeliveryNote:
      'Your first digest arrives next time the source updates, not before.',
    firstDeliveryLink: 'How digests work',
    samplesHead: 'Sample digests',
    latestDelivery: 'Latest digest',
    sendToMe: 'Send it to me',
    sentTo: (email) => `Sent to ${email}.`,
    // --- about page (was the help page; renamed 2026-09) ---
    help: 'About',
    helpTitle: 'About Punktlista',
    helpGreeting: 'Hi! I am the one who built this.',
    helpIntro:
      'After building a few other tools, I noticed that what gets published out there is full of interesting information that hardly anyone has time to read. So I built something that gives me, and others, three points from everything we follow. Give it a try if you have not already! Questions and ideas are welcome at my email address.',
    helpSign: 'Osama',
    helpMailCta: 'Email me',
    helpPhotoAlt: 'Osama Eldawebi, who builds Punktlista',
    helpFaqHead: 'Common questions',
    helpFaq: [
      {
        q: 'How does it work?',
        a: 'Pick a source under Sources and enter your email address. The next time that source updates, we read it and email you three points from it, each with its page number and a link that opens the pdf on the right page.',
      },
      {
        q: 'When does my first digest arrive?',
        a: 'When the source you picked updates next. How often that is varies by source, from monthly to once a year, and each row in the list under Sources says which. Until you have picked a source, the app shows a few real digests from sources we already follow.',
      },
      {
        q: 'What does it cost?',
        a: 'Your first source is free and only needs an email address. To follow more sources there are two plans with a fixed monthly price. You will find them under Your plan in the top bar. Cancel anytime.',
      },
      {
        q: 'How do I cancel?',
        a: 'For the free source, just click Stop following under Sources and the digests stop right away. A paid plan is cancelled in the Stripe customer portal, which you reach from your receipt email. Or email me and I will do it for you.',
      },
      {
        q: 'Missing a source you want to follow?',
        a: 'Paste the page into the app under Sources. If that does not work, you can send a request right there. Adding a new source by hand usually takes me a day.',
      },
    ],
    heroTitle: 'Three bullets from everything you follow',
    heroMark: 'three bullets',
    heroCta: 'Pick your free source',
    proofHead: 'What a digest looks like',
    wallLabel: 'Publishers we follow',
    seeSample: 'See a digest',
    // Subject line of the example mail. The report title goes inside the
    // sentence rather than after a dash or a second colon: several of the
    // titles already carry a colon of their own.
    mailSubject: (title) => `Three takeaways from ${title}`,
    stripCaption: '19 sources from 15 publishers today.',
    closeTitle: 'Your first source is free.',
    priceNote: 'All of Swedish housing. Cancel anytime.',
    sheetFoot: 'Cancel anytime. Payments go through Stripe. Questions? ',
    helpPageLink: 'See the help page.',
    startHead: 'Get started',
    startSteps: [
      'Pick a source',
      'Enter your email',
      'Get the next digest',
    ],
    welcomeLine: 'Pick your first source. It is free.',
    activating: 'Activating',
    removing: 'Removing',
    pickDone: (report, publisher, email) =>
      `Done! The next ${report} from ${publisher} goes to ${email}.`,
    pickCheckMail: (email) =>
      `Check your inbox: we sent a link to ${email}. Open it to start following.`,
    offlineNote: 'We cannot reach the server right now. This shows what you followed last time.',
    notFoundTitle: 'Page not found.',
    notFoundBody: 'The address may be misspelled, or the page has moved or been removed.',
    notFoundHome: 'Go to the home page',
    notFoundPublishers: 'See the publishers we follow',
    // --- own sources (T6.3, generalised in the follow-anything plan) ---
    ownFeedHead: 'Follow a source',
    ownFeedOpen: 'Follow a source',
    ownFeedHint:
      'Paste the address of a source you want to follow, such as an agency news page or a press room. We will pick up what is new for you.',
    ownFeedUrlPlaceholder: 'https://example.com/news',
    ownFeedUrlLabel: 'Address of the source',
    ownFeedEmailLabel: 'Email for this source',
    ownFeedFreqLabel: 'How often',
    ownFeedSubmit: 'Add',
    ownFeedPending: 'Checking the source…',
    ownFeedTag: 'Your own source',
    ownFeedEmptyUrl: 'Paste the source address.',
    ownFeedLoginNudge: 'Log in to follow your own sources.',
    sourceNotSupported: 'This source is not supported yet.',
    upcomingKind: (name) => `${name} is coming soon. Tell me if you want it first.`,
    // --- coming soon (sources tab, C-upcoming) ---
    upcomingHead: 'Coming soon',
    upcomingNote: 'You can ask for these already. ',
    upcomingRequestLink: 'Send a request',
    // --- series requests ---
    requestHead: 'Want me to add it by hand?',
    requestNotePlaceholder: 'Which publisher or source is it?',
    requestNoteLabel: 'Tell me more',
    requestSubmit: 'Send request',
    requestPending: 'Sending…',
    requestThanks: 'Thanks! I will let you know when it can be followed.',
    requestOpen: 'Missing a publisher? Send a request',
    requestNeedNote: 'Tell me which publisher you are missing.',
    // --- plan table / upgrade sheet (RapportvaktPage.jsx) ---
    chooseTier: (name) => `Choose ${name}`,
    unlockTitle: (report) => `Unlock ${report ?? 'more sources'}`,
    unlockBody: (name) =>
      `You already have your free source. ${name ?? 'This source'} is included in both plans below.`,
    fromJoiner: ' from ',
    emailForLabel: (report) => `Email for ${report}`,
    // --- sample and digest pages (RapportvaktSamplePage.jsx, RapportvaktDigestPage.jsx) ---
    sampleIntroStrong: 'This is a real digest from Punktlista.',
    sampleIntroBody:
      ' When a source updates, subscribers get the three most important points, each with its page number and a link that opens the pdf on the right page.',
    moreExamples: 'More examples:',
    andJoiner: ' and ',
    ctaTitle: 'Want this for a source you follow?',
    ctaBody: (count, amount, period) =>
      `We follow ${count} sources in Swedish housing and construction today. Your first source is free, after that ${amount} ${period}. Questions? `,
    weeklyNote:
      'Every Monday: three points from last week’s statistics releases from SCB, with quotes and a direct link. Source: SCB.',
    weeklyAllWeek: (week, count) => `Everything SCB published in week ${week} (${count} statistics releases)`,
    weeklyAll: (count) => `Everything SCB published this week (${count} statistics releases)`,
    prevWeek: 'Previous week',
    nextWeek: 'Next week',
    otherWeeks: 'Other weeks',
    // --- point visual (PointVisual.jsx) ---
    benchmarkLabel: 'Benchmark: ',
  },
};

export function publisherInitials(publisher) {
  const words = publisher.replace('/', ' ').split(/\s+/).filter(Boolean);
  if (words.length === 1) return words[0].slice(0, 2).toUpperCase();
  return (words[0][0] + words[1][0]).toUpperCase();
}

// --- own sources (T6.3, generalised in the follow-anything plan) -----------
//
// A reader's own source only ever offers three delivery frequencies —
// separate vocabulary from the catalogue's RAPPORTVAKT_FREQUENCIES buckets
// (vecka, manad, kvartal, ar, oregelbundet), which describe a publisher's
// release cadence, not a subscription's delivery rate. These ids match the
// backend's SubscriptionIn.frequency values.
export const OWN_FEED_FREQUENCIES = [
  { id: 'daily', label: 'Varje dag', labelEn: 'Daily' },
  { id: 'weekly', label: 'Varje vecka', labelEn: 'Weekly' },
  { id: 'monthly', label: 'Varje månad', labelEn: 'Monthly' },
];

/** 'weekly' -> 'varje vecka', the lowercase form used next to a source row. */
export function ownFeedFrequencyLabel(frequency) {
  const found = OWN_FEED_FREQUENCIES.find((f) => f.id === frequency);
  return found ? found.label.toLowerCase() : '';
}
